/** The text a dictation has produced so far, placed in a draft at the caret where it started. The text of
 *  each item is interim while it streams and is replaced by its final text; items are separated by single
 *  spaces, and the whole is separated from its neighbours the same way. What it inserted is remembered, so
 *  `withdraw` removes exactly that and nothing the person typed. */
export class DictationDraft {
  private items = new Map<string, string>();
  private detached = new Set<string>();
  private inserted = '';
  private value?: string;
  private selection?: { start: number; end: number };
  constructor(private start: number) {}

  delta(itemId: string, text: string, value: string, caret: number): { value: string; caret: number } {
    this.edit(value, caret);
    if (this.detached.has(itemId)) return { value, caret };
    this.items.set(itemId, (this.items.get(itemId) ?? '') + text);
    return this.compose(value, caret);
  }
  final(itemId: string, text: string, value: string, caret: number): { value: string; caret: number } {
    this.edit(value, caret);
    if (this.detached.has(itemId)) return { value, caret };
    this.items.set(itemId, text);
    return this.compose(value, caret);
  }

  /** Native beforeinput selection anchors the edit even when neighbouring text is identical. */
  beforeEdit(start: number, end: number): void { this.selection = { start, end }; }

  /** Observe each draft change: edits before the owned range shift it; intersecting edits relinquish it.
   *  Compare the previous draft, never search for the inserted words elsewhere. */
  edit(value: string, caret: number): void {
    const previous = this.value;
    const selection = this.selection;
    this.value = value; this.selection = undefined;
    if (previous === undefined || (previous === value && !selection)) return;
    let start = 0;
    const prefixLimit = selection ? Math.min(selection.start, caret) : Math.min(previous.length, value.length);
    while (start < prefixLimit && previous[start] === value[start]) start++;
    let oldEnd = previous.length;
    let newEnd = value.length;
    const oldLimit = selection ? Math.max(start, selection.end) : start;
    const newLimit = selection ? Math.max(start, caret) : start;
    while (oldEnd > oldLimit && newEnd > newLimit && previous[oldEnd - 1] === value[newEnd - 1]) {
      oldEnd--; newEnd--;
    }
    if (oldEnd <= this.start) this.start += value.length - previous.length;
    else if (start < this.start + this.inserted.length) {
      for (const itemId of this.items.keys()) this.detached.add(itemId);
      this.items.clear();
      this.start = caret; this.inserted = '';
    }
  }

  private place(value: string, caret: number, text: string): { value: string; caret: number } {
    this.edit(value, caret);
    const before = value.slice(0, this.start);
    const after = value.slice(this.start + this.inserted.length);
    const lead = text && before && !/\s$/.test(before) ? ' ' : '';
    const tail = text && after && !/^\s/.test(after) ? ' ' : '';
    this.inserted = text ? `${lead}${text}${tail}` : '';
    this.value = `${before}${this.inserted}${after}`;
    return { value: this.value, caret: this.start + lead.length + text.length };
  }

  /** `value` with this dictation's text applied, and the caret after it. */
  private compose(value: string, caret: number): { value: string; caret: number } {
    const text = [...this.items.values()].map((item) => item.trim()).filter(Boolean).join(' ');
    return this.place(value, caret, text);
  }

  /** `value` without the text this dictation inserted. */
  withdraw(value: string, caret: number): { value: string; caret: number } {
    return this.place(value, caret, '');
  }
}
